import { BrandConfig } from '@/config/brand.config';
import { SearchService } from '@/plugins/services/search/index';
import { Variant, Facet } from '@/types/products';
import { SearchRequestPayload } from '@/types/search';
import { LanguageConfig } from '@/types/config';

const VARIANT_ATTRIBUTE_NAME = 'variant';
const PRODUCT_ATTRIBUTE_NAME = 'product';
const COMMON_VARIANT_BRANDS_ATTRIBUTE_NAME = 'commonVariantBrands';
const COMMON_VARIANT_ATTRIBUTES_ATTRIBUTE_NAME = 'commonVariantAttributes';

export const SIZE_DESCRIPTION_ATTRIBUTE_NAME = 'thgsize';
export const SUPPLIER_ITEM_NUMBER_ATTRIBUTE_NAME = 'thgsupplieritemnumber';
export const STOCKED_AT_NAME = 'Lagerförd';
export const SUNDAHUS_LEVEL = 'thgsundahuslevel';
export const HIDDEN_ATTRIBUTES: string[] = [SUNDAHUS_LEVEL];

const getUpperLevelFacets = (upperLevelFacetNames: string[], facets: Facet[]) =>
  upperLevelFacetNames.map((name) => {
    const existingFacet = facets.find((facet) => facet.attributeName === name);

    return existingFacet || ({ attributeName: name } as Facet);
  });

const removeDuplicates = (facets: Facet[]) => {
  const facetMap = facets.reduce((map, facet) => {
    map[facet.attributeName] = facet;

    return map;
  }, {} as { [key: string]: Facet });

  return Object.values(facetMap);
};

export const makeSearchRequest = async (
  $searchService: ReturnType<typeof SearchService>,
  brandConfig: BrandConfig,
  isDirectSearch: boolean,
  payload: SearchRequestPayload,
  trackingId: string,
  assortmentId = 'default',
) => {
  const mandatoryFacetNames = brandConfig.mandatoryFacetNames ?? [];
  const { body } = payload;
  const { query } = body;

  const { skip, take, sortBy, facets } = body.resultsOptions;
  const validFacets = facets.map(({ attributeName, name, selected, type }) => {
    // Make sure to only use properties that is used by the resultsOptions spec.
    return { attributeName, name, selected, type };
  });

  const upperLevelFacets = getUpperLevelFacets(mandatoryFacetNames, facets);

  const customData = {
    directSearch: isDirectSearch,
  };

  const filter = {
    type: 'attribute',
    attributeName: 'assortmentIds',
    value: assortmentId,
    comparisonMode: 'contains',
  };

  // The order of returned facets is determined by the order in which they are
  // sent. Facets with a unspecified position are put last.
  const data = {
    resultsOptions: { skip, take, sortBy, filter, facets: removeDuplicates(upperLevelFacets.concat(validFacets)) },
    query,
    customData,
  };

  const response = await $searchService.search(data, trackingId);

  return response;
};

export const makeGetByAttributeRequest = (
  $searchService: ReturnType<typeof SearchService>,
  brandConfig: BrandConfig,
  payload: SearchRequestPayload,
  trackingId: string,
  assortmentId = 'default',
) => {
  const mandatoryFacetNames = brandConfig.mandatoryFacetNames ?? [];
  const { body } = payload;
  const { attribute } = body;
  const { skip, take, sortBy, facets } = body.resultsOptions;
  const validFacets = facets.map(({ attributeName, name, selected, type }) => {
    // Make sure to only use properties that is used by the resultsOptions spec.
    return { attributeName, name, selected, type };
  });

  const limitFacets =
    attribute.value.split('/').length < brandConfig.limitFacetsLevel && attribute.name !== 'campaignIds';

  const upperLevelFacetNames = limitFacets ? [...mandatoryFacetNames, 'filterTags', 'stockedAt'] : mandatoryFacetNames;

  const upperLevelFacets = getUpperLevelFacets(upperLevelFacetNames, facets);

  const filter = {
    type: 'attribute',
    attributeName: 'assortmentIds',
    value: assortmentId,
    comparisonMode: 'contains',
  };

  // The order of returned facets is determined by the order in which they are
  // sent. Facets with a unspecified position are put last.
  const data = {
    attribute,
    resultsOptions: {
      skip,
      take,
      sortBy,
      filter,
      facets: limitFacets ? upperLevelFacets : removeDuplicates(upperLevelFacets.concat(validFacets)),
    },
    customData: {
      limitFacets,
    },
  };

  return $searchService.searchByAttribute(data, trackingId);
};

export const filterAttributesByName = (attributes: Facet[], attributeName: string) => {
  return attributes.filter((attribute) => attribute.name === attributeName);
};

export const filterAndSortAttributes = (languageConfig: LanguageConfig, unsortedAttributes: Facet[]) => {
  if (!languageConfig?.attributes?.length || !languageConfig?.labels || !Object.entries(languageConfig.labels).length) {
    return unsortedAttributes;
  }

  const sortedAttributes: Facet[] = [];

  languageConfig.attributes.forEach((attributeName) => {
    const attribute = unsortedAttributes.find((unsortedAttribute) => unsortedAttribute.attributeName === attributeName);

    if (attribute) {
      const label = languageConfig.labels[attribute.attributeName];
      const sortedAttribute = {
        attributeName: attribute.attributeName,
        attributeValue: attribute.attributeValue,
        attributeLabel: label,
      };

      sortedAttributes.push(sortedAttribute);
    }
  });

  return sortedAttributes;
};

export const parseProducts = (languageConfig: LanguageConfig, serializedProducts: Variant[]) => {
  const parsedProducts = serializedProducts.map((element) => {
    const variantsArray = filterAttributesByName(element.attributes, VARIANT_ATTRIBUTE_NAME);

    const productArray = filterAttributesByName(element.attributes, PRODUCT_ATTRIBUTE_NAME);

    if (!productArray.length || !productArray[0].values.length || !variantsArray.length) {
      throw new Error('No products found!');
    }

    const deserializedVariants = variantsArray[0].values.map((variant) => {
      const deserializedVariant = JSON.parse(variant);
      deserializedVariant.attributes = filterAndSortAttributes(languageConfig, deserializedVariant.attributes);

      return deserializedVariant;
    });

    const deserializedProduct = JSON.parse(productArray[0].values[0]);
    deserializedProduct.totalVariants = deserializedProduct.variants?.length || 0;
    deserializedProduct.variants = deserializedVariants;
    deserializedProduct.commonVariantAttributes = filterAndSortAttributes(
      languageConfig,
      deserializedProduct.commonVariantAttributes,
    );

    return deserializedProduct;
  });

  return parsedProducts;
};

export const directSearch = async ($searchService, brandConfig: BrandConfig, payload, options) => {
  const { data, error } = await makeSearchRequest(
    $searchService,
    brandConfig,
    true,
    payload,
    options.trackingId,
    options.assortmentId,
  );

  if (error) throw error;

  const { items, count } = data.results;
  const { spellingSuggestions } = data;

  const results = [];
  items.forEach((item) => {
    let variant;
    let commonVariantAttributes;
    let commonVariantBrands;

    for (const attribute of item.attributes) {
      if (attribute.name === VARIANT_ATTRIBUTE_NAME) {
        variant = JSON.parse(attribute.values[0]);
      } else if (attribute.name === COMMON_VARIANT_ATTRIBUTES_ATTRIBUTE_NAME) {
        commonVariantAttributes = attribute.values.map((value) => JSON.parse(value));
      } else if (attribute.name === COMMON_VARIANT_BRANDS_ATTRIBUTE_NAME) {
        commonVariantBrands = attribute.values.map((value) => JSON.parse(value));
      }
    }

    // Check if the variant has commonVariantBrands or commonVariantAttributes
    if (commonVariantAttributes) {
      // Add the products shared attributes to the variants attributes
      variant.attributes.push(...commonVariantAttributes);
    }

    if (commonVariantBrands) {
      // Variants does not have brands set, use commonVariantBrands to set it
      variant.brands = commonVariantBrands;
    }

    // Sort and filter attributes
    variant.attributes = filterAndSortAttributes(options.languageConfig, variant.attributes);

    results.push(variant);
  });

  return { results, count, spellingSuggestions };
};

export const nonDirectSearch = async ($searchService, brandConfig: BrandConfig, payload, options) => {
  // eslint-disable-next-line no-useless-catch
  try {
    const response = await makeSearchRequest(
      $searchService,
      brandConfig,
      false,
      payload,
      options.trackingId,
      options.assortmentId,
    );
    const { items, count, facets } = response.data.results;
    // eslint-disable-next-line camelcase
    const { VariantResults_TotalItems, facetEntitiesData, side } = response.data.customData;
    const { spellingSuggestions, makesSense } = response.data;

    const results = parseProducts(options.languageConfig, items);

    const suggestions =
      !makesSense && spellingSuggestions.count > 0
        ? spellingSuggestions.items.map((suggestion) => suggestion.query)
        : [];

    return {
      results,
      count,
      spellingSuggestions: suggestions,
      // eslint-disable-next-line camelcase
      VariantResults_TotalItems,
      facets,
      facetsData: facetEntitiesData,
      side,
    };
  } catch (error) {
    throw error;
  }
};

export const getProductsByAttribute = async (
  $searchService,
  brandConfig: BrandConfig,
  payload,
  options: { trackingId: string; assortmentId: string; languageConfig: any },
) => {
  const { data, error } = await makeGetByAttributeRequest(
    $searchService,
    brandConfig,
    payload,
    options.trackingId,
    options.assortmentId,
  );

  if (error) throw error;

  const { items, count, facets } = data.results;

  // eslint-disable-next-line camelcase
  const { VariantResults_TotalItems, facetEntitiesData, side } = data.customData;

  const products = parseProducts(options.languageConfig, items);
  // eslint-disable-next-line camelcase
  return { products, count, VariantResults_TotalItems, facets, facetsData: facetEntitiesData, side };
};

export const sortAttributes = (a: string, b: string, sortAcsending = true, clearWhitespace = true) => {
  // put empty strings last

  let comparableA = a;
  let comparableB = b;

  if (clearWhitespace) {
    // In some cases, the attributes have whitespace separated numbers
    comparableA = /\d/.test(a) ? a.replaceAll(/\s/g, '') : a;
    comparableB = /\d/.test(b) ? b.replaceAll(/\s/g, '') : b;
  }

  if (a === b) {
    return 0;
  } else if (!a) {
    return 1;
  } else if (!b) {
    return -1;
  } else if (sortAcsending) {
    return comparableA.localeCompare(comparableB, undefined, { numeric: true });
  } else {
    return comparableB.localeCompare(comparableA, undefined, { numeric: true });
  }
};

export const sortAttributesWithSequencing = (objectsToSort: any, sequence: string[], nameField: string) => {
  const facetMap: Map<string, number> = new Map(sequence?.map((name: string, index: number) => [name, index]));
  const sorted = objectsToSort.sort((a: any, b: any) => {
    const indexA = facetMap.get(a[nameField] as string) ?? Number.MAX_VALUE;
    const indexB = facetMap.get(b[nameField] as string) ?? Number.MAX_VALUE;
    return indexA - indexB;
  });
  return sorted;
};
